import { useEffect, useMemo, useState, type RefObject } from "react";
import { LoaderCircleIcon } from "lucide-react";
import {
  promptAssistancePreviewParts,
  promptPreviewWordCount,
} from "../../state/promptAssistancePreview";
import { Button } from "../ui/button";
import { cn } from "../../lib/utils";
import type { PromptAssistanceState } from "./promptAssistanceReview";

type Review = Extract<PromptAssistanceState, { completedPrompt: string | null }>;
const wordNumbers = new Intl.NumberFormat();

export function PromptRewritePreview({
  review,
  originalPrompt,
  previewRef,
  onRestore,
}: {
  review: Review;
  originalPrompt: string;
  previewRef: RefObject<HTMLDivElement | null>;
  onRestore: () => void;
}) {
  const [showOriginal, setShowOriginal] = useState(false);
  const text = showOriginal ? originalPrompt : review.prompt;
  const parts = useMemo(() => promptAssistancePreviewParts(text), [text]);
  const wordCount = useMemo(() => promptPreviewWordCount(parts), [parts]);
  useEffect(() => {
    if (review.phase === "enhancing") setShowOriginal(false);
  }, [review.phase]);
  useEffect(() => {
    if (previewRef.current) previewRef.current.scrollTop = 0;
  }, [showOriginal, previewRef]);

  return (
    <>
      <div className="mx-4 mt-2 flex min-h-8 shrink-0 items-center justify-between gap-2">
        <div role="group" aria-label="Compare prompts" className="flex items-center gap-0.5">
          {([false, true] as const).map((original) => (
            <Button
              key={String(original)}
              variant="ghost-muted"
              size="sm"
              aria-pressed={showOriginal === original}
              className={cn(
                "text-xs sm:text-xs",
                showOriginal === original && "bg-accent text-foreground",
              )}
              onClick={() => setShowOriginal(original)}
            >
              {original ? "Original" : "Rewrite"}
            </Button>
          ))}
        </div>
        <span className="flex items-center gap-1.5 whitespace-nowrap text-[11px] tabular-nums text-muted-foreground">
          {review.phase === "enhancing" && !showOriginal ? (
            <>
              <LoaderCircleIcon
                className="size-3 animate-spin motion-reduce:animate-none"
                aria-hidden="true"
              />
              Writing…
            </>
          ) : text ? (
            `${wordNumbers.format(wordCount)} ${wordCount === 1 ? "word" : "words"}`
          ) : null}
        </span>
      </div>
      <div
        ref={previewRef}
        tabIndex={0}
        role="region"
        aria-label={showOriginal ? "Original prompt text" : "Suggested prompt text"}
        data-testid="enhanced-prompt-text"
        aria-busy={!showOriginal && review.phase === "enhancing"}
        className="mx-4 my-3 min-h-0 overflow-y-auto overscroll-contain whitespace-pre-wrap break-words text-[13px] leading-relaxed text-foreground/90 focus-visible:outline-2 focus-visible:outline-ring"
      >
        {!showOriginal && review.phase === "error" ? (
          <p role="alert" className="text-muted-foreground">
            Couldn’t finish the rewrite. Try again or keep your draft.
          </p>
        ) : null}
        {!showOriginal && review.phase === "enhancing" && !review.prompt ? (
          <p className="text-muted-foreground">Adding useful detail to your request…</p>
        ) : null}
        {parts.map((part, index) =>
          part.kind === "text" ? (
            <span key={index}>{part.text}</span>
          ) : part.kind === "reference" ? (
            <span key={index} className="rounded bg-primary/5 px-1 py-0.5 text-xs font-medium">
              {part.label}
            </span>
          ) : (
            <blockquote key={index} className="my-2 border-l-2 border-primary/25 pl-3">
              <p className="text-muted-foreground">{part.text}</p>
              {part.comment ? <p className="mt-1">{part.comment}</p> : null}
            </blockquote>
          ),
        )}
        {!showOriginal && review.phase !== "preview" && review.completedPrompt !== null ? (
          <Button variant="link" size="sm" className="mt-3 px-0 text-xs" onClick={onRestore}>
            Restore rewrite
          </Button>
        ) : null}
      </div>
      <div role="status" className="sr-only">
        {review.phase === "enhancing"
          ? "Enhancing your draft"
          : review.phase === "preview"
            ? "Rewrite ready to review"
            : "Enhancement failed"}
      </div>
    </>
  );
}
